// src/components/EventTimeline.tsx
import React, { useState, useEffect } from 'react';
import { mockDataService, ApplicationEvent } from '../services/MockDataService';

interface EventTimelineProps {
  phoneNumber?: string; // Filter by specific customer
  onBack?: () => void;
  onCustomerSelect?: (phoneNumber: string) => void;
  onTicketSelect?: (ticketId: string) => void;
}

const EventTimeline: React.FC<EventTimelineProps> = ({ 
  phoneNumber, 
  onBack, 
  onCustomerSelect, 
  onTicketSelect 
}) => {
  const [events, setEvents] = useState<ApplicationEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterPhone, setFilterPhone] = useState(phoneNumber || '');
  const [filterEventType, setFilterEventType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUserType, setFilterUserType] = useState('');

  // Unique values for filters
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [userTypes, setUserTypes] = useState<string[]>([]);
  const [statuses] = useState(['success', 'failed', 'pending', 'in_progress']);

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        const eventsData = await mockDataService.getEvents(filterPhone || undefined, 200);
        setEvents(eventsData);
        
        // Extract unique values for filter dropdowns
        const uniqueEventTypes = [...new Set(eventsData.map(e => e.event_type))].sort();
        const uniqueUserTypes = [...new Set(eventsData.map(e => e.user_type))].sort();
        
        setEventTypes(uniqueEventTypes);
        setUserTypes(uniqueUserTypes);
      } catch (error) {
        console.error('Error loading events:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [filterPhone]);

  // Filter events based on selected filters
  const filteredEvents = events.filter(event => {
    if (filterEventType && event.event_type !== filterEventType) return false;
    if (filterStatus && event.status !== filterStatus) return false;
    if (filterUserType && event.user_type !== filterUserType) return false;
    return true;
  });

  const getEventStatusIcon = (status: ApplicationEvent['status']) => {
    const icons = {
      success: '✅',
      failed: '❌',
      pending: '⏳',
      in_progress: '🔄'
    };
    return icons[status] || '❓';
  };

  const getEventTypeIcon = (eventType: string) => {
    const icons: Record<string, string> = {
      customer_inquiry_started: '📱',
      ticket_created: '🎫',
      providers_contacted: '👥',
      job_offer_received: '📧',
      job_accepted: '✅',
      job_declined: '❌',
      payment_required: '💳',
      payment_completed: '💰',
      payment_failed: '💥',
      service_completed: '🏆',
      registration_started: '📝',
      registration_completed: '✨',
      error_payment: '🚨',
      error_provider: '⚠️',
      negotiation_offered: '🤝',
      negotiation_accepted: '🎯',
      negotiation_declined: '🚫'
    };
    return icons[eventType] || '📋';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatEventType = (eventType: string) => {
    return eventType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getRelativeTime = (dateString: string) => {
    const now = new Date();
    const eventTime = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - eventTime.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const clearFilters = () => {
    setFilterEventType('');
    setFilterStatus('');
    setFilterUserType('');
  };

  const exportEvents = () => {
    const csv = [
      ['Timestamp', 'Phone Number', 'Event Type', 'Status', 'User Type', 'Ticket ID', 'Provider ID'],
      ...filteredEvents.map(event => [
        event.created_at,
        event.phone_number,
        event.event_type,
        event.status,
        event.user_type,
        event.ticket_id || '',
        event.provider_id || ''
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `events-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center">
              {onBack && (
                <button 
                  onClick={onBack}
                  className="mr-4 text-gray-500 hover:text-gray-700"
                >
                  ← Back
                </button>
              )}
              <h1 className="text-2xl font-bold text-gray-900">
                📅 Event Timeline
              </h1>
              {phoneNumber && (
                <span className="ml-3 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {phoneNumber}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                {filteredEvents.length} of {events.length} events
              </span>
              <button
                onClick={exportEvents}
                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm font-medium"
              >
                📊 Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Clear All
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="text"
                value={filterPhone}
                onChange={(e) => setFilterPhone(e.target.value)}
                placeholder="e.g. +1234567890"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Type
              </label>
              <select
                value={filterEventType}
                onChange={(e) => setFilterEventType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                {eventTypes.map(type => (
                  <option key={type} value={type}>{formatEventType(type)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                {statuses.map(status => (
                  <option key={status} value={status}>{status.toUpperCase()}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User Type
              </label>
              <select
                value={filterUserType}
                onChange={(e) => setFilterUserType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Users</option>
                {userTypes.map(type => (
                  <option key={type} value={type}>{type.toUpperCase()}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              {loading && (
                <div className="flex items-center text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Loading...
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="text-3xl mr-3">✅</div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {filteredEvents.filter(e => e.status === 'success').length}
                </div>
                <div className="text-sm text-gray-600">Successful</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="text-3xl mr-3">❌</div>
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {filteredEvents.filter(e => e.status === 'failed').length}
                </div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="text-3xl mr-3">⏳</div>
              <div>
                <div className="text-2xl font-bold text-yellow-600">
                  {filteredEvents.filter(e => e.status === 'pending').length}
                </div>
                <div className="text-sm text-gray-600">Pending</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="text-3xl mr-3">👥</div>
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {new Set(filteredEvents.map(e => e.phone_number)).size}
                </div>
                <div className="text-sm text-gray-600">Unique Users</div>
              </div>
            </div>
          </div>
        </div>

        {/* Events List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Event Timeline ({filteredEvents.length})
            </h3>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading events...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="p-8 text-center">
              <span className="text-4xl mb-4 block">📭</span>
              <p className="text-gray-500">No events found matching your filters.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {filteredEvents.map((event, index) => (
                <div
                  key={`${event.event_timestamp}-${index}`}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start space-x-4">
                    
                    {/* Event Icon */}
                    <div className="flex-shrink-0 flex flex-col items-center">
                      <div className="text-2xl">
                        {getEventTypeIcon(event.event_type)}
                      </div>
                      <div className="text-lg mt-1">
                        {getEventStatusIcon(event.status)}
                      </div>
                    </div>

                    {/* Event Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">
                          {formatEventType(event.event_type)}
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">
                            {getRelativeTime(event.created_at)}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatDate(event.created_at)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          event.status === 'success' ? 'bg-green-100 text-green-800' :
                          event.status === 'failed' ? 'bg-red-100 text-red-800' :
                          event.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {event.status.toUpperCase()}
                        </span>
                        
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {event.user_type.toUpperCase()}
                        </span>

                        <button
                          onClick={() => onCustomerSelect?.(event.phone_number)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {event.phone_number}
                        </button>

                        {event.ticket_id && (
                          <button
                            onClick={() => onTicketSelect?.(event.ticket_id!)} // ✅ use non-null assertion if safe
                            className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                          >
                            {event.ticket_id}
                          </button>
                        )}

                        {event.provider_id && (
                          <span className="text-xs text-gray-600">
                            Provider: {event.provider_id}
                          </span>
                        )}
                      </div>

                      {/* Metadata (if any) */}
                      {event.metadata && Object.keys(event.metadata).length > 0 && (
                        <div className="mt-2 text-xs text-gray-600">
                          <details className="group">
                            <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                              View metadata →
                            </summary>
                            <div className="mt-1 p-2 bg-gray-50 rounded text-xs font-mono">
                              {JSON.stringify(event.metadata, null, 2)}
                            </div>
                          </details>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          Events are displayed in reverse chronological order (newest first).
          Use filters above to narrow down results for specific debugging scenarios.
        </div>

      </div>
    </div>
  );
};

export default EventTimeline;